import type {Nullable} from "@skbkontur/react-ui/typings/utility-types.js";
import type {
  FiasAddressObject,
  FiasHouse, FiasId,
  FiasObject,
  FiasRoom,
  FiasStead,
  GarHouse
} from "./common.ts";
import {
  FiasEstateStatuses,
  FiasFlatType, FiasRoomType,
  FiasStructureStatuses,
  GarHouseAddType,
  GarHouseType
} from "./common.js";

export class FiasData {
  public static isAddressObject = (data: Nullable<FiasObject>): data is FiasAddressObject => {
    return Boolean(
      data &&
      Object.prototype.hasOwnProperty.call(data, 'name') &&
      Object.prototype.hasOwnProperty.call(data, 'abbreviation')
    );
  };

  public static isStead = (data: Nullable<FiasObject>): data is FiasStead => {
    return Boolean(
      data &&
      Object.prototype.hasOwnProperty.call(data, 'number') &&
      Object.prototype.hasOwnProperty.call(data, 'liveStatus') &&
      !Object.prototype.hasOwnProperty.call(data, 'estateStatus')
    );
  };

  public static isHouse = (data: Nullable<FiasObject>): data is FiasHouse => {
    return Boolean(
      data &&
      Object.prototype.hasOwnProperty.call(data, 'structureStatus') &&
      (Object.prototype.hasOwnProperty.call(data, 'number') ||
        Object.prototype.hasOwnProperty.call(data, 'structureNumber') ||
        Object.prototype.hasOwnProperty.call(data, 'buildingNumber'))
    );
  };

  public static isGarHouse = (data: Nullable<FiasObject>): data is GarHouse => {
    return Boolean(
      data && (Object.prototype.hasOwnProperty.call(data, 'houseNum') ||
      Object.prototype.hasOwnProperty.call(data, 'houseType'))
    );
  };

  public static isRoom = (data: Nullable<FiasObject>): data is FiasRoom => {
    return Boolean(
      data &&
      Object.prototype.hasOwnProperty.call(data, 'flatType') &&
      Object.prototype.hasOwnProperty.call(data, 'flatNumber')
    );
  };

  public static isRegion = (data: Nullable<FiasObject>): boolean => {
    return Boolean(data && Object.prototype.hasOwnProperty.call(data, 'level') && data.level === 'Region');
  };

  constructor(public data: FiasObject) {}

  public get name(): string {
    const { data } = this;
    if (FiasData.isAddressObject(data)) {
      return data.name;
    } else if (FiasData.isStead(data)) {
      return data.number;
    } else if (FiasData.isHouse(data)) {
      return data.number || data.structureNumber || data.buildingNumber || '';
    } else if (FiasData.isGarHouse(data)) {
      return data.houseNum || '';
    } else if (FiasData.isRoom(data)) {
      return data.flatNumber;
    }
    return '';
  }

  public get official(): string {
    if (FiasData.isAddressObject(this.data) && FiasData.isRegion(this.data)) {
      const { name, abbreviation, official } = this.data;
      switch (abbreviation) {
        case 'г': {
          return official.replace(/Город/, 'город');
        }

        case 'обл.':
          return `${name} область`;

        default:
          return official;
      }
    } else {
      return '';
    }
  }

  public get abbreviation(): string {
    return FiasData.isAddressObject(this.data) ? this.data.abbreviation : '';
  }

  public get code(): string {
    return (FiasData.isAddressObject(this.data) && this.data.code) || '';
  }

  public get number(): string {
    return (FiasData.isStead(this.data) || FiasData.isHouse(this.data)) && this.data.number ? this.data.number : '';
  }

  public get structureNumber(): string {
    return FiasData.isHouse(this.data) && this.data.structureNumber ? this.data.structureNumber : '';
  }

  public get buildingNumber(): string {
    return FiasData.isHouse(this.data) && this.data.buildingNumber ? this.data.buildingNumber : '';
  }

  public get fiasId(): FiasId {
    return this.data.fiasId;
  }

  public get parentFiasIds(): FiasId[] {
    if (!this.data) {
      return [];
    }
    const { parentFiasId, parentFiasIds } = this.data;
    return parentFiasIds || (parentFiasId ? [parentFiasId] : []);
  }

  public get postalCode(): string {
    return (this.data && this.data.postalCode) || '';
  }

  public get estateStatus(): FiasEstateStatuses | undefined {
    if (FiasData.isHouse(this.data)) {
      return this.data.estateStatus;
    }
    return undefined;
  }

  public get structureStatus(): FiasStructureStatuses | undefined {
    if (FiasData.isHouse(this.data)) {
      return this.data.structureStatus;
    }
    return undefined;
  }

  public get houseNum(): string | undefined {
    if (FiasData.isGarHouse(this.data)) {
      return this.data.houseNum;
    }
    return undefined;
  }

  public get houseType(): GarHouseType | undefined {
    if (FiasData.isGarHouse(this.data)) {
      return this.data.houseType;
    }
    return undefined;
  }

  public get addNum1(): string | undefined {
    if (FiasData.isGarHouse(this.data)) {
      return this.data.addNum1;
    }
    return undefined;
  }

  public get addNum2(): string | undefined {
    if (FiasData.isGarHouse(this.data)) {
      return this.data.addNum2;
    }
    return undefined;
  }

  public get addType1(): GarHouseAddType | undefined {
    if (FiasData.isGarHouse(this.data)) {
      return this.data.addType1;
    }
    return undefined;
  }

  public get addType2(): GarHouseAddType | undefined {
    if (FiasData.isGarHouse(this.data)) {
      return this.data.addType2;
    }
    return undefined;
  }

  public get flatNumber(): string | undefined {
    if (FiasData.isRoom(this.data)) {
      return this.data.flatNumber;
    }
    return undefined;
  }

  public get flatType(): string | undefined {
    if (FiasData.isRoom(this.data)) {
      // verification expects flat type as string
      // https://git.skbkontur.ru/portal/portal/-/blob/4da72bd9ca/Portal.DataModel/Fias/Entities/VerifiableRoom.cs#L14
      return FiasFlatType[this.data.flatType];
    }
    return undefined;
  }

  public get flatFiasId(): FiasId | undefined {
    if (FiasData.isRoom(this.data)) {
      return this.data.flatFiasId;
    }
    return undefined;
  }

  public get roomNumber(): string | undefined {
    if (FiasData.isRoom(this.data) && this.flatFiasId) {
      return this.data.roomNumber;
    }
    return undefined;
  }

  public get roomType(): string | undefined {
    if (FiasData.isRoom(this.data) && this.flatFiasId && this.data.roomType) {
      // verification expects room type as string
      // https://git.skbkontur.ru/portal/portal/-/blob/4da72bd9ca/Portal.DataModel/Fias/Entities/VerifiableRoom.cs#L23
      return FiasRoomType[this.data.roomType];
    }
    return undefined;
  }
}
